import { MutableRefObject } from 'react';
import type {
  ConfigurationFormMethods,
  ConfigurationFormValues,
} from 'src/pages/ConfigurationPage/hooks/useConfigurationForm';
import type { Workflow } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';
import type { PersonaInputData } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';
import type {
  ConfigurationPrompt,
  ConfigurationSettings,
  ConfigurationTemplateFields,
  useCreateConfigurationSettingsMutation,
} from 'src/services/configurationService';
import { buildPersonaSettingsPayload } from 'src/pages/ConfigurationPage/utils/buildPersonaSettingsPayload';
import type { ConfigurationFormFieldsObject } from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';

// Builds and dispatches the create/update request for either the persona
// form or the system-prompt form, guarding against double submission.
export const useConfigurationSubmit = ({
  systemPromptActive,
  formFieldsObject,
  inputData,
  isCreationMode,
  activeSettings,
  configurationSettingsPrompt,
  defaultTemplateFields,
  defaultWorkflows,
  customWorkflows,
  id,
  methods,
  createConfigurationSettings,
  updateConfigurationSettings,
  isSubmitting,
}: {
  systemPromptActive: boolean;
  formFieldsObject: ConfigurationFormFieldsObject;
  inputData: PersonaInputData | null;
  isCreationMode: boolean;
  activeSettings: ConfigurationSettings | undefined;
  configurationSettingsPrompt: ConfigurationPrompt | undefined;
  defaultTemplateFields: ConfigurationTemplateFields | undefined;
  defaultWorkflows: Workflow[];
  customWorkflows: Workflow[];
  id: string;
  methods: ConfigurationFormMethods;
  createConfigurationSettings: ReturnType<typeof useCreateConfigurationSettingsMutation>[0];
  updateConfigurationSettings: (args: {
    project_id: string;
    settings_id: number;
    body: Record<string, unknown>;
  }) => unknown;
  isSubmitting: MutableRefObject<boolean>;
}) => {
  const submitPersonaForm = (formData: ConfigurationFormValues) => {
    const payload = buildPersonaSettingsPayload(formData, formFieldsObject, inputData);
    const input_data = {
      ...payload,
      pf_default_workflow: defaultWorkflows[0],
      pf_custom_workflows: customWorkflows,
    };

    if (isCreationMode) {
      const body: { prompt: Record<string, unknown> } = { prompt: { input_data } };

      if (activeSettings?.is_deployed) {
        body.prompt.prompt_id = configurationSettingsPrompt.id;
      } else {
        body.prompt.template_id = defaultTemplateFields.id;
      }

      createConfigurationSettings({ id, body });
    } else {
      updateConfigurationSettings({
        project_id: id,
        settings_id: activeSettings.id,
        body: { input_data },
      });
    }
  };

  const submitSystemPromptForm = ({
    text,
    selected_template_id,
    placeholder,
  }: {
    text?: string;
    selected_template_id?: string | number;
    placeholder?: string;
  }) => {
    let prompt: { template_id: number; input_data: Record<string, unknown> } | undefined;

    methods.setValue('template_id', 'custom');

    if (selected_template_id) {
      prompt = {
        template_id: +selected_template_id,
        input_data: { [placeholder]: text },
      };
    } else if (configurationSettingsPrompt) {
      const promptPlaceholder = Object.keys(configurationSettingsPrompt.input_data)[0];

      prompt = {
        template_id: configurationSettingsPrompt.template_id,
        input_data: { [promptPlaceholder]: text },
      };
    }

    if (isCreationMode) {
      createConfigurationSettings({ id, body: { prompt } });
    } else {
      updateConfigurationSettings({
        project_id: id,
        settings_id: activeSettings.id,
        body: { ...prompt, text },
      });
    }
  };

  const submitForm = (formData: ConfigurationFormValues) => {
    if (isSubmitting.current) {
      return;
    }

    isSubmitting.current = true;

    if (systemPromptActive) {
      submitSystemPromptForm(formData);
    } else {
      submitPersonaForm(formData);
    }
  };

  return { submitForm };
};
